import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, Chip, createStyles, FormControl, FormControlLabel, FormHelperText, Grid, makeStyles,
    Paper, Radio, RadioGroup, TextField, Theme, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ClientModel } from '../../../Models/ClientModel';
import ClientService from '../../../Services/ClientService';
import { VehicleModel, vehicleModelInitial } from '../../../Models/VehicleModel';
import { CostTime } from './CostTime';
import { VehicleContext } from '../VehicleContext';
import { CountryModel } from '../../../Models/CountryModel';
import { StateModel } from '../../../Models/StateModel';
import StateService from '../../../Services/StateService';
import CountryService from '../../../Services/CountryService';
import CirculationService from '../../../Services/CirculationService';
import { CirculationModel } from '../../../Models/CirculationModel';
import EditorialCategoryService from '../../../Services/EditorialCategoryService';
import { EditorialCategoryModel } from '../../../Models/EditorialCategoryModel';
import { MediaModel } from '../../../Models/MediaModel';
import MediaService from '../../../Services/MediaService';
import { COUNTRY_BRASIL_ID } from '../../../shared/constants';
import { CityModel } from '../../../Models/CityModel';
import CityService from '../../../Services/CityService';
import VehicleService from '../../../Services/VehicleService';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { numberFormat } from '../../../shared/operators';
import { costCentimeterModelInitial } from '../../../Models/CostCentimeterModel';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { HomeContext } from '../../Home/HomeContext';
import { EditAction } from '../../../shared/enum/EditAction';
import { BasicPage } from '../../../shared/components/BasicPage';
import { VehicleIpm } from '../components/VehicleIpm';
import { CostType } from '../../../shared/enum/CostType';

const useStyles = makeStyles((theme: Theme) => createStyles({
    itens: {
        width: '100%',
        color: 'primary',
    },
    paper: {
        padding: theme.spacing(3),
    },
    editionButtons: {
        display: 'flex',
        width: '100%',
        textAlign: 'right',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginBottom: theme.spacing(2)
    }
}));

export function VehicleInput() {
    const classes = useStyles();

    const homeContext = React.useContext(HomeContext);
    const vehicleContext = React.useContext(VehicleContext);

    const [clientList, setClientList] = React.useState<ClientModel[]>([]);
    const [vehicleClients, setVehicleClients] = React.useState<ClientModel[]>([]);
    const [countryList, setCountryList] = React.useState<CountryModel[]>([]);
    const [stateList, setStateList] = React.useState<StateModel[]>([]);
    const [cityList, setCityList] = React.useState<CityModel[]>([]);
    const [circulationList, setCirculationList] = React.useState<CirculationModel[]>([]);
    const [editorialCategoryList, setEditorialCategoryList] = React.useState<EditorialCategoryModel[]>([]);
    const [mediaList, setMediaList] = React.useState<MediaModel[]>([]);

    const [countrySelect, setCountrySelect] = React.useState<CountryModel | null>(null);
    const [stateSelect, setStateSelect] = React.useState<StateModel | null>(null);
    const [citySelect, setCitySelect] = React.useState<CityModel | null>(null);
    const [circulationSelect, setCirculationSelect] = React.useState<CirculationModel | null>(null);
    const [editorialCategorySelect, setEditorialCategorySelect] = React.useState<EditorialCategoryModel | null>(null);
    const [mediaSelect, setMediaSelect] = React.useState<MediaModel | null>(null);

    const [nameNotDefinedError, setNameNotDefinedError] = React.useState<boolean>(false);
    const [mediaNotDefinedError, setMediaNotDefinedError] = React.useState<boolean>(false);
    const [broadcasterNotDefinedError, setBroadcasterNotDefinedError] = React.useState<boolean>(false);
    const [broadcasterMaskNotDefinedError, setBroadcasterMaskNotDefinedError] = React.useState<boolean>(false);
    const [circulationNotDefinedError, setCirculationNotDefinedError] = React.useState<boolean>(false);
    const [identificationNotDefinedError, setIdentificationNotDefinedError] = React.useState<boolean>(false);
    const [countryNotDefinedError, setCountryNotDefinedError] = React.useState<boolean>(false);
    const [stateNotDefinedError, setStateNotDefinedError] = React.useState<boolean>(false);
    const [cityNotDefinedError, setCityNotDefinedError] = React.useState<boolean>(false);
    const [editorialCategoryNotDefinedError, setEditorialCategoryNotDefinedError] = React.useState<boolean>(false);
    const [advertisingCostNotDefinedError, setAdvertisingCostNotDefinedError] = React.useState<boolean>(false);
    const [clientNotDefinedError, setClientNotDefinedError] = React.useState<boolean>(false);

    const [centimeterReachNotDefinedError, setCentimeterReachNotDefinedError] = React.useState<boolean>(false);
    const [centimeterPriceNotDefinedError, setCentimeterPriceNotDefinedError] = React.useState<boolean>(false);

    const [isVehicleExistsDialogOpen, setIsVehicleExistsDialogOpen] = React.useState<boolean>(false);
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = React.useState<boolean>(false);
    const [edit, setEdit] = React.useState(false);

    React.useEffect(() => {
        fetchMedia();
        fetchCirculation();
        fetchCountry();
        fetchEditorialCategory();
        fetchClients();

        if (homeContext.pageLocation === PageLocation.VEHICLE_EDIT) {
            fetchEditVehicle();
            setEdit(true);
        } else
            setEdit(false);
    }, []);

    React.useEffect(() => {
        if (homeContext.pageLocation != PageLocation.VEHICLE_EDIT)
            vehicleContext.setVehicle(vehicleModelInitial);
    }, [homeContext.pageLocation]);

    React.useEffect(() => {
        if (edit && countryList && countryList.length > 0
            && vehicleContext.vehicle && vehicleContext.vehicle.countryId) {

            const foundCountry = countryList.find(c => c.id == vehicleContext.vehicle.countryId);
            const vehicle = { ...vehicleContext.vehicle };
            if (foundCountry) {
                setCountrySelect(foundCountry);

                fetchState(foundCountry, (res: AxiosResponse) => {
                    setStateList(res.data);
                    const states: StateModel[] = res.data;

                    if (states && states.length > 0
                        && vehicleContext.vehicle && vehicleContext.vehicle.stateId) {

                        const foundState = states.find(c => c.id == vehicleContext.vehicle.stateId);
                        if (foundState) {
                            setStateSelect(foundState);
                            fetchCity(foundState, (res: AxiosResponse) => {
                                setCityList(res.data);

                                if (vehicleContext.vehicle.cityName) {
                                    const cities: CityModel[] = res.data;
                                    const foundCity = cities.find(c => c.nome == vehicleContext.vehicle.cityName);
    
                                    if (foundCity)
                                        setCitySelect(foundCity);
                                }
                                
                                vehicle.country = foundCountry;
                                vehicle.state = foundState;
                                vehicleContext.setVehicle(vehicle);
                            });
                        } else
                            setStateSelect(null);
                    }
                });

                vehicle.country = foundCountry;
                vehicleContext.setVehicle(vehicle);
            }
        }
    }, [countryList, vehicleContext.vehicle.countryId, vehicleContext.vehicle.stateId]);

    const fetchMedia = () => {
        homeContext.setOpenLoadingDialog(true);
                
        MediaService.mediaService()
            .getAll()
            .then((res: AxiosResponse) => { setMediaList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Mídia. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const handleClientsChange = (value: ClientModel[]) => {
        const array: ClientModel[] = [];
        value?.forEach(client => {
            const found = vehicleContext.vehicle.clients.find(c => c.id == client.id);
            if (found) { 
                if (found.editAction == EditAction.DELETE)
                    array.push({...found, editAction: EditAction.NONE});
                else
                    array.push({...found});
            }
            else
                array.push({...client, editAction: EditAction.INSERT});            
        });
        vehicleContext.vehicle.clients.forEach(client => {
            if (client) {
                const foundClient = array.find(c => c.id == client.id);

                if (!foundClient && client.editAction != EditAction.INSERT)
                    array.push({...client, editAction: EditAction.DELETE});
            }
        });
        setVehicleClients(array.filter(c => c.editAction != EditAction.DELETE));
        vehicleContext.setVehicle({ ...vehicleContext.vehicle, clients: array });
    };

    const fetchCirculation = () => {
        homeContext.setOpenLoadingDialog(true);

        CirculationService.circulationService()
            .getAll()
            .then((res: AxiosResponse) => { setCirculationList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Circulação. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchCountry = () => {
        homeContext.setOpenLoadingDialog(true);

        CountryService.countryService()
            .getAll()
            .then((res: AxiosResponse) => { setCountryList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com País. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchState = (country: CountryModel, callback: (res: AxiosResponse) => void) => {
        homeContext.setOpenLoadingDialog(true);

        StateService.stateService(country.id)
            .getAll()
            .then((res: AxiosResponse) => { callback(res); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Estado. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchCity = (state: StateModel, callback: (res: AxiosResponse) => void) => {
        homeContext.setOpenLoadingDialog(true);

        CityService.cityService(state.initials.substring(0, 2))
            .then((res: AxiosResponse) => { callback(res); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Cidade. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchEditorialCategory = () => {
        homeContext.setOpenLoadingDialog(true);

        EditorialCategoryService.editorialCategoryService()
            .getAll()
            .then((res: AxiosResponse) => { setEditorialCategoryList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Categoria Editorial. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchClients = () => {
        homeContext.setOpenLoadingDialog(true);

        ClientService.clientService()
            .getAll()
            .then((res: AxiosResponse) => { setClientList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Cliente. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchEditVehicle = () => {
        homeContext.setOpenLoadingDialog(true);

        VehicleService.vehicleService()
            .get(vehicleContext.vehicle.id)
            .then((res: AxiosResponse) => {
                vehicleContext.setVehicle(res.data);
                setCirculationSelect(res.data.circulation);
                setEditorialCategorySelect(res.data.editorialCategory);
                setMediaSelect(res.data.media);
                setVehicleClients(res.data.clients);                
            })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. não foi possível carregar as informações do veículo <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false);});
    };

    const hasError = () => {
        let error = false;

        const nameError = !vehicleContext.vehicle.name;
        const mediaError = !vehicleContext.vehicle.media;
        const circulationError = !vehicleContext.vehicle.circulation;
        const identificationError = !vehicleContext.vehicle.identification;
        const countryError = !vehicleContext.vehicle.country;
        const stateError = !vehicleContext.vehicle.state;
        const cityError = !vehicleContext.vehicle.cityName;
        const editorialCategoryError = !vehicleContext.vehicle.editorialCategory;
        const advertisingCostError = !((vehicleContext.vehicle.advertisingCost != null) && (vehicleContext.vehicle.advertisingCost > -1));
        const clientError = !(vehicleContext.vehicle.clients.length && vehicleContext.vehicle.clients.length > 0);
        
        setNameNotDefinedError(nameError);
        setMediaNotDefinedError(mediaError);
        setCirculationNotDefinedError(circulationError);
        setIdentificationNotDefinedError(identificationError);
        setCountryNotDefinedError(countryError);
        setStateNotDefinedError(stateError);
        setCityNotDefinedError(cityError);
        setEditorialCategoryNotDefinedError(editorialCategoryError);
        setAdvertisingCostNotDefinedError(advertisingCostError);
        setClientNotDefinedError(clientError);

        error = nameError ||
            mediaError ||
            circulationError ||
            identificationError ||
            countryError ||
            stateError ||
            cityError ||
            editorialCategoryError ||
            advertisingCostError ||
            clientError;

        if (vehicleContext.vehicle.media && vehicleContext.vehicle.media.costType === CostType.COST_TIME) {

            const broadcasterError = !vehicleContext.vehicle.broadcasterName;
            const broadcasterMaskError = !vehicleContext.vehicle.broadcasterMaskName;

            setBroadcasterNotDefinedError(broadcasterError);
            setBroadcasterMaskNotDefinedError(broadcasterMaskError);

            error = error ||
                broadcasterError ||
                broadcasterMaskError;
        }

        if (vehicleContext.vehicle.media && vehicleContext.vehicle.media.costType === CostType.COST_CENTIMETER) {
                
            const centimeterReach = !((vehicleContext.vehicle.costCentimeter?.reach != null)
                && (vehicleContext.vehicle.costCentimeter?.reach > -1));
                
            const centimeterPrice = !((vehicleContext.vehicle.costCentimeter?.centimeterPrice != null)
                && (vehicleContext.vehicle.costCentimeter?.centimeterPrice > -1));
                
            setCentimeterReachNotDefinedError(centimeterReach);
            setCentimeterPriceNotDefinedError(centimeterPrice);

            error = error ||
                centimeterReach ||
                centimeterPrice;
        }

        return error;
    };

    const hasCostTimeError = () => {
        if (vehicleContext.vehicle.media && vehicleContext.vehicle.media.costType === CostType.COST_TIME) {
            if (!(vehicleContext.vehicle.costTime
                && vehicleContext.vehicle.costTime.length > 0
                && vehicleContext.vehicle.costTime.find(c => c.editAction != EditAction.DELETE))) {

                homeContext.showError('Ops.. você precisa inserir pelo menos um custo tempo para poder salvar o veículo. ');

                return true;
            }
        }

        return false;
    };

    const resetSelectedFields = () => {
        vehicleContext.setVehicle(vehicleModelInitial);
        setVehicleClients([]);
        setCountrySelect(null);
        setStateSelect(null);
        setCitySelect(null);
        setCirculationSelect(null);
        setEditorialCategorySelect(null);
        setMediaSelect(null);
        homeContext.setOpenLoadingDialog(false);
    };

    const handleSave = () => {
        const error = hasError();
        const costTimeError = hasCostTimeError();
        if (!error && !costTimeError) {
            homeContext.setOpenLoadingDialog(true);

            if (edit) {
                VehicleService.vehicleService().update(vehicleContext.vehicle)
                    .then(() => {
                        homeContext.showSuccess('O veículo foi alterado com sucesso');
                        closeEdit();
                    })
                    .catch((error: AxiosError) => {
                        homeContext.showError('Ops.. ocorreu um erro e não foi possível alterar o veículo.' +
                            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                    })
                    .finally(()=> homeContext.setOpenLoadingDialog(false));
            } else {
                VehicleService.vehicleExists(vehicleContext.vehicle)
                    .then((res: AxiosResponse) => {
                        if (res.data.length > 0) {
                            setIsVehicleExistsDialogOpen(true);
                            homeContext.setOpenLoadingDialog(false);
                        }
                        else
                            postVehicle();
                    })
                    .catch((error: AxiosError) => {
                        homeContext.showError('Ops.. ocorreu um erro e não foi possível alterar o veículo.' +
                            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                    })
                    .finally(()=> homeContext.setOpenLoadingDialog(false));
            }
        }
    };

    const closeEdit = () => {
        resetSelectedFields();
        homeContext.setPageLocation(PageLocation.VEHICLE_SEARCH);
    };

    const postVehicle = () => {
        homeContext.setOpenLoadingDialog(true);
        
        VehicleService.postVehicle(vehicleContext.vehicle)
            .then(() => {
                homeContext.showSuccess('O novo veículo foi incluído com sucesso!');
                resetSelectedFields();
            })
            .catch((error: AxiosError) => {
                homeContext.showError('Ops.. ocorreu um erro e não foi possível salvar o novo veículo.' +
                    'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
            })
            .finally(()=> homeContext.setOpenLoadingDialog(false));
    };

    const handleChange = (prop: keyof VehicleModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        vehicleContext.setVehicle({ ...vehicleContext.vehicle, [prop]: event.target.value });
    };

    const handleNumberChange = (prop: keyof VehicleModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (numberFormat(event.target.value) > -1 || numberFormat(event.target.value) != -1){
            const number = numberFormat(event.target.value);
            vehicleContext.setVehicle({ ...vehicleContext.vehicle, [prop]: number });
        }
    };

    const getName = () => {
        return (
            <Grid item xs={12} md={8}>
                <TextField
                    id='vehicle-name'
                    fullWidth
                    label="Nome do veículo"
                    variant="outlined"
                    className={classes.itens }
                    value={vehicleContext.vehicle.name}
                    onChange={handleChange('name')}
                    error={nameNotDefinedError && !vehicleContext.vehicle.name}
                    helperText={ (nameNotDefinedError && !vehicleContext.vehicle.name) && 'Campo obrigatório' } />
            </Grid>
        );
    };

    const getMedia = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-media'
                    className={classes.itens}
                    value={mediaSelect}
                    disabled={edit}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={mediaList}
                    getOptionLabel={(option: MediaModel) => option.name}
                    onChange={(_, value: MediaModel | null) => {
                        setMediaSelect(value);
                        const vehicle = { ...vehicleContext.vehicle };

                        if (vehicle.media && vehicle.media.costType === CostType.COST_TIME && value) {
                            vehicle.costCentimeter = undefined;
                        }
                        else if (vehicle.media && vehicle.media.costType === CostType.COST_CENTIMETER && value) {
                            vehicle.broadcasterName = undefined;
                            vehicle.broadcasterMaskName = undefined;
                            vehicle.costTime = [];
                        }
                        else {
                            vehicle.broadcasterName = undefined;
                            vehicle.broadcasterMaskName = undefined;
                            vehicle.costTime = [];
                            vehicle.costCentimeter = undefined;
                        }

                        vehicle.media = value;
                        vehicleContext.setVehicle(vehicle);
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Tipo de mídia"
                            variant="outlined"
                            error={mediaNotDefinedError && !mediaSelect}
                            helperText={mediaNotDefinedError && !mediaSelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getBroadcasterByMedia = () => {
        if (vehicleContext.vehicle.media && vehicleContext.vehicle.media.costType === CostType.COST_TIME) {
            return (
                <>
                    { getBroadcaster() }
                    { getBroadcasterMask() }
                </>
            );
        }
    };

    const getBroadcaster = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    id='vehicle-broadcaster'
                    fullWidth
                    label="Emissora"
                    variant="outlined"
                    className={classes.itens}
                    value={vehicleContext.vehicle.broadcasterName ? vehicleContext.vehicle.broadcasterName : ''}
                    onChange={handleChange('broadcasterName')}
                    error={broadcasterNotDefinedError && !vehicleContext.vehicle.broadcasterName}
                    helperText={ (broadcasterNotDefinedError && !vehicleContext.vehicle.broadcasterName) && 'Campo obrigatório' } />
            </Grid>
        );
    };

    const getBroadcasterMask = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    id='vehicle-broadcaster-mask'
                    fullWidth
                    label="Máscara emissora"
                    variant="outlined"
                    className={classes.itens}
                    value={vehicleContext.vehicle.broadcasterMaskName ? vehicleContext.vehicle.broadcasterMaskName : ''}
                    onChange={handleChange('broadcasterMaskName')}
                    error={broadcasterMaskNotDefinedError && !vehicleContext.vehicle.broadcasterMaskName}
                    helperText={ (broadcasterMaskNotDefinedError && !vehicleContext.vehicle.broadcasterMaskName) && 'Campo obrigatório' } />
            </Grid>
        );
    };

    const getCirculation = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-circulation'
                    className={classes.itens}
                    value={circulationSelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={circulationList}
                    getOptionLabel={(option: CirculationModel) => option.name}
                    onChange={(_, value: CirculationModel | null) => {
                        setCirculationSelect(value);
                        vehicleContext.setVehicle({ ...vehicleContext.vehicle, circulation: value });
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Circulação"
                            variant="outlined"
                            error={circulationNotDefinedError && !circulationSelect}
                            helperText={circulationNotDefinedError && !circulationSelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getIdentification = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Typography variant='subtitle1'>Identificação</Typography>
                <FormControl component="fieldset" size='small'>
                    <RadioGroup
                        row
                        aria-label="identification"
                        name="vehicle-identification"
                        value={vehicleContext.vehicle.identification}
                        onChange={handleChange('identification')}>
                        <FormControlLabel
                            value="Nacional"
                            control={<Radio color='primary' size='small' />}
                            label={<Typography variant="body1" color="textPrimary">Nacional</Typography>} />
                        <FormControlLabel
                            value="Internacional"
                            control={<Radio color='primary' size='small' />}
                            label={<Typography variant="body1" color="textPrimary">Internacional</Typography>} />
                    </RadioGroup>
                    {identificationNotDefinedError && !vehicleContext.vehicle.identification && (
                        <FormHelperText error id="error-not-select">Campo obrigatório</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    };

    const getCountry = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-country'
                    className={classes.itens}
                    value={countrySelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={countryList}
                    getOptionLabel={(option: CountryModel) => option.name}
                    onChange={(_, value: CountryModel | null) => {
                        setCountrySelect(value);

                        setStateSelect(null);
                        setCitySelect(null);

                        const vehicle = { ...vehicleContext.vehicle };
                        vehicle.cityName = '';

                        if (value) {
                            fetchState(value, (res: AxiosResponse) => setStateList(res.data));
                            vehicle.country = value;
                        }
                        else {
                            setStateList([]);
                            vehicle.country = null;
                        }

                        vehicleContext.setVehicle(vehicle);

                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="País"
                            variant="outlined"
                            error={countryNotDefinedError && !countrySelect}
                            helperText={countryNotDefinedError && !countrySelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getState = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-uf'
                    disabled={countrySelect == null}
                    className={classes.itens}
                    value={stateSelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={stateList}
                    getOptionLabel={(option: StateModel) => `${option.initials} - ${option.description}`}
                    onChange={(_, value: StateModel | null) => {
                        setStateSelect(value);

                        setCitySelect(null);

                        const vehicle = { ...vehicleContext.vehicle };
                        vehicle.cityName = '';

                        if (value) {
                            fetchCity(value, (res: AxiosResponse) => setCityList(res.data));
                            vehicle.state = value;
                        }
                        else {
                            setCityList([]);
                            vehicle.state = null;
                        }

                        vehicleContext.setVehicle(vehicle);
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="UF"
                            variant="outlined"
                            error={stateNotDefinedError && !stateSelect}
                            helperText={stateNotDefinedError && !stateSelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getCity = () => {
        if (vehicleContext.vehicle.country && vehicleContext.vehicle.country.metricCode == COUNTRY_BRASIL_ID)
            return getAutocompleteCity();

        return getTextfieldCity();
    };

    const getAutocompleteCity = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-city-autocomplete'
                    disabled={stateSelect == null}
                    className={classes.itens}
                    value={citySelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={cityList}
                    getOptionLabel={(option: CityModel) => option ? option.nome : ''}
                    onChange={(_, value: CityModel | null) => {
                        setCitySelect(value);
                        if (value)
                            vehicleContext.setVehicle({ ...vehicleContext.vehicle, cityName: value.nome });
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Cidade"
                            variant="outlined"
                            error={cityNotDefinedError && !vehicleContext.vehicle.cityName}
                            helperText={(cityNotDefinedError && !vehicleContext.vehicle.cityName) && 'Campo obrigatório'} />
                    } />
            </Grid>
        );
    };

    const getTextfieldCity = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <TextField
                    id='vehicle-city-text'
                    fullWidth
                    disabled={stateSelect == null}
                    label="Cidade"
                    variant="outlined"
                    className={classes.itens}
                    value={vehicleContext.vehicle.cityName}
                    onChange={handleChange('cityName')}
                    error={cityNotDefinedError && !vehicleContext.vehicle.cityName}
                    helperText={(cityNotDefinedError && !vehicleContext.vehicle.cityName) && 'Campo obrigatório'} />
            </Grid>
        );
    };

    const getEditalCategory = () => {
        return (
            <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    id='vehicle-editorial-category'
                    className={classes.itens}
                    value={editorialCategorySelect}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    options={editorialCategoryList}
                    getOptionLabel={(option: EditorialCategoryModel) => option.name}
                    onChange={(_, value: EditorialCategoryModel | null) => {
                        setEditorialCategorySelect(value);
                        vehicleContext.setVehicle({ ...vehicleContext.vehicle, editorialCategory: value });
                    }}
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Categoria editorial"
                            variant="outlined"
                            error={editorialCategoryNotDefinedError && !editorialCategorySelect}
                            helperText={editorialCategoryNotDefinedError && !editorialCategorySelect ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getAdvertisingCost = () => {
        return <Grid item xs={12} sm={6} md={4}>
            <TextField
                id='vehicle-advertising-cost'
                fullWidth
                label="Custo publicitário"
                variant="outlined"
                type="number"
                className={classes.itens}
                value={vehicleContext.vehicle.advertisingCost
                    ? vehicleContext.vehicle.advertisingCost
                    : (vehicleContext.vehicle.advertisingCost === 0 ? 0 : '')}
                onChange={handleNumberChange('advertisingCost')}
                error={advertisingCostNotDefinedError &&
                    !((vehicleContext.vehicle.advertisingCost != null) && (vehicleContext.vehicle.advertisingCost > -1))}
                helperText={(advertisingCostNotDefinedError &&
                    !((vehicleContext.vehicle.advertisingCost != null) && (vehicleContext.vehicle.advertisingCost > -1))) &&
                    'O valor não pode ser vazio ou negativo.'} />
        </Grid>;
    };

    const getCostByMedia = () => {
        if (vehicleContext.vehicle.media) {
            if (vehicleContext.vehicle.media.costType === CostType.COST_TIME)
                return <CostTime />;

            if (vehicleContext.vehicle.media.costType === CostType.COST_CENTIMETER)
                return getCostCentimeter();
        }
    };

    const getCostCentimeter = () => {
        return (
            <Grid item xs={12} className={classes.itens}>
                <Paper className={classes.paper}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6">
                                Custo centimetragem
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id='vehicle-centimeter-reach'
                                fullWidth
                                label="Alcance"
                                variant="outlined"
                                type="number"
                                className={classes.itens}
                                value={vehicleContext.vehicle.costCentimeter && vehicleContext.vehicle.costCentimeter?.reach
                                    ? vehicleContext.vehicle.costCentimeter?.reach
                                    : (vehicleContext.vehicle.costCentimeter?.reach === 0 ? 0 : '')}
                                onChange={(event) => {                           
                                    if (numberFormat(event.target.value) > -1 || numberFormat(event.target.value) != -1){
                                        const vehicleCostCentimeter = vehicleContext.vehicle.costCentimeter
                                            ? { ...vehicleContext.vehicle.costCentimeter }
                                            : costCentimeterModelInitial;
                                        vehicleCostCentimeter.reach = numberFormat(event.target.value);
                                        vehicleContext.setVehicle({ ...vehicleContext.vehicle, costCentimeter: vehicleCostCentimeter });
                                    }
                                }}
                                error={centimeterReachNotDefinedError &&
                                !((vehicleContext.vehicle.costCentimeter?.reach != null) && (vehicleContext.vehicle.costCentimeter?.reach > -1))}
                                helperText={(centimeterReachNotDefinedError &&
                                !((vehicleContext.vehicle.costCentimeter?.reach != null) && (vehicleContext.vehicle.costCentimeter?.reach > -1))) &&
                                'O valor não pode ser vazio ou negativo.'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id='vehicle-centimeter-price'
                                fullWidth
                                label="Valor da centimetragem"
                                variant="outlined"
                                type="number"
                                className={classes.itens}
                                value={vehicleContext.vehicle.costCentimeter && vehicleContext.vehicle.costCentimeter?.centimeterPrice
                                    ? vehicleContext.vehicle.costCentimeter?.centimeterPrice
                                    : (vehicleContext.vehicle.costCentimeter?.centimeterPrice === 0 ? 0 : '')}
                                onChange={(event) => {
                                    if (numberFormat(event.target.value) > -1 || numberFormat(event.target.value) != -1){
                                        const vehicleCostCentimeter = vehicleContext.vehicle.costCentimeter
                                            ? { ...vehicleContext.vehicle.costCentimeter }
                                            : costCentimeterModelInitial;
                                        vehicleCostCentimeter.centimeterPrice = numberFormat(event.target.value);
                                        vehicleContext.setVehicle({ ...vehicleContext.vehicle, costCentimeter: vehicleCostCentimeter });
                                    }
                                }}
                                error={centimeterPriceNotDefinedError &&
                                    !((vehicleContext.vehicle.costCentimeter?.centimeterPrice != null) && (vehicleContext.vehicle.costCentimeter?.centimeterPrice > -1))}
                                helperText={(centimeterPriceNotDefinedError &&
                                    !((vehicleContext.vehicle.costCentimeter?.centimeterPrice != null) && (vehicleContext.vehicle.costCentimeter?.centimeterPrice > -1))) &&
                                    'O valor não pode ser vazio ou negativo.'}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        );
    };

    const getClient = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id='vehicle-editorial-category'
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    value={vehicleClients}
                    onChange={(_, value: ClientModel[]) => { handleClientsChange(value); }}
                    options={clientList ? clientList : []}
                    getOptionLabel={(option: ClientModel) => option.name}
                    filterSelectedOptions
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Clientes"
                            variant="outlined"
                            error={clientNotDefinedError && !(vehicleContext.vehicle.clients.length > 0)}
                            helperText={clientNotDefinedError && !(vehicleContext.vehicle.clients.length > 0) ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getIpm = () => {
        return (
            <Grid item xs={12} className={classes.itens}>
                <VehicleIpm />
            </Grid>
        );
    };

    const getSaveButton = () => {
        return (
            <Grid item xs={12}>
                {edit && (
                    <Grid
                        container
                        spacing={1}
                        className={classes.editionButtons} >
                        <Grid item>
                            <Button
                                variant="outlined"
                                onClick={() => setIsDiscardDialogOpen(true)}
                                size='large'>
                                DESCARTAR
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color='primary'
                                onClick={handleSave}
                                size='large'>
                                SALVAR ALTERAÇÕES
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {!edit && (
                    <Grid
                        container
                        direction="column"
                        justify="flex-end"
                        alignItems="flex-end">
                        <Button
                            variant="contained"
                            color='primary'
                            onClick={handleSave}
                            size='large'>
                            SALVAR VEÍCULO
                        </Button>
                    </Grid>
                )}
            </Grid>
        );
    };

    const getModalDialogs = () => {
        return (
            <>
                <BasicConfirmDialog
                    title='Ops, esse veículo já existe.'
                    description={ `Encontramos o veículo ${vehicleContext.vehicle.name} já
                        cadastrado na base. Deseja incluir o novo veículo mesmo assim?` }
                    handleFirstButton={() => { postVehicle(); setIsVehicleExistsDialogOpen(false); }}
                    handleSecondButton={() => setIsVehicleExistsDialogOpen(false)}
                    textFirstButton="Incluir mesmo assim"
                    textSecondButton="Descartar"
                    open={isVehicleExistsDialogOpen} />
                <BasicConfirmDialog
                    title='Descartar as alterações realizadas?'
                    description={'Ao descartar as alterações do veículo serão perdidas.'}
                    handleFirstButton={() => setIsDiscardDialogOpen(false)}
                    handleSecondButton={() => { closeEdit(); setIsDiscardDialogOpen(false); }}
                    textFirstButton="Manter"
                    textSecondButton="Descartar"
                    open={isDiscardDialogOpen} />
            </>
        );
    };

    return (
        <BasicPage
            title={edit ? 'Editar veículo' : 'Cadastro de veículo'}
            subtitle={ edit
                ? 'Edite os campos que deseja alterar para o veículo.'
                : 'Para cadastrar um veículo, insira os dados solicitados abaixo.' }
            dialogs={getModalDialogs()}>
            <>
                {getName()}
                {getMedia()}
                {getBroadcasterByMedia()}
                {getCirculation()}
                {getIdentification()}
                {getCountry()}
                {getState()}
                {getCity()}
                {getEditalCategory()}
                {getAdvertisingCost()}
                {getCostByMedia()}
                {getIpm()}
                {getClient()}
                {getSaveButton()}
            </>
        </BasicPage>
    );
}